import { DomSanitizer, SafeValue } from '@angular/platform-browser';

import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Injectable } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Observable, Subject } from 'rxjs';
import { ConfirmationDialogSettings, SanitizeType } from 'src/app/shared/models/confirmation-dialog-settings.model';
import { SafedomPipe } from 'src/app/shared/pipes/safedom.pipe';

@Component({
    template: `
        <div [attr.data-qa-id]="dataQaId">
            <h1
                mat-dialog-title
                data-qa-id="DialogTitle">
                {{ title || action }}
                @if (needCross) {
                    <button
                        class="nova__button--icon"
                        data-qa-id="CloseCrossButton"
                        (click)="close()">
                        <mat-icon>close</mat-icon>
                    </button>
                }
            </h1>
            <div
                class="dialog-content"
                mat-dialog-content
                data-qa-id="ConfirmDialogContent">
                <div
                    data-qa-id="DialogContentMessage"
                    [innerHtml]="message"></div>
            </div>
            <mat-dialog-actions data-qa-id="ConfirmDialogActions">
                <button
                    (click)="confirm()"
                    mat-flat-button
                    color="primary"
                    data-qa-id="SuccessButton"
                    [mat-dialog-close]="true">
                    {{ action | uppercase }}
                </button>
                @if (needCancel) {
                    <button
                        mat-flat-button
                        [color]="isTertiaryCancel ? 'primary' : 'cancel'"
                        data-qa-id="CancelButton"
                        (click)="close()"
                        [class.tertiary]="isTertiaryCancel">
                        {{ cancelText | uppercase }}
                    </button>
                }
            </mat-dialog-actions>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [
        `
            .mat-mdc-dialog-actions {
                padding: 0;
                min-height: auto;
            }
            .mat-mdc-dialog-actions[align='end'] {
                justify-content: flex-end;
            }
            .mat-mdc-dialog-content.mdc-dialog__content.dialog-content[data-qa-id='ConfirmDialogContent'] {
                padding-top: 24px;
                padding-bottom: 16px;
            }
        `
    ],
    standalone: true,
    imports: [MatIconModule, MatDialogModule, MatButtonModule, UpperCasePipe]
})
export class ConfirmDialogComponent {
    message: SafeValue | string = '';

    action = '';

    onClose: Subject<boolean> = new Subject<boolean>();

    title = '';

    needCancel = true;

    needCross = false;

    cancelText: string | undefined = 'Cancel';

    type: SanitizeType = SanitizeType.html;

    needExtraPadding = true;

    dataQaId = '';

    isTertiaryCancel = false;

    alignButtonWithText = false;

    constructor(
        private readonly dialogRef: MatDialogRef<ConfirmDialogComponent>,
        private readonly domSanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA)
        private readonly data: {
            message: SafeValue | string;
            action: string;
            title: string;
            needCancel?: boolean;
            cancelText?: string;
            needCross?: boolean;
            type?: SanitizeType;
            needExtraPadding?: boolean;
            dataQaId?: string;
            isTertiaryCancel?: boolean;
            alignButtonWithText?: boolean;
        }
    ) {
        if (
            data.type === SanitizeType.html ||
            data.type === SanitizeType.style ||
            data.type === SanitizeType.script ||
            data.type === SanitizeType.url ||
            data.type === SanitizeType.resourceUrl
        ) {
            const pipe = new SafedomPipe(this.domSanitizer);
            this.message = pipe.transform(data.message, data.type);
        } else {
            this.message = data.message;
        }

        this.action = data.action;
        this.title = data.title;
        this.cancelText = data.cancelText;

        if (!this.cancelText) {
            this.cancelText = 'Cancel';
        }

        if (data.needCancel !== undefined) {
            this.needCancel = data.needCancel;
        }

        if (data.needCross !== undefined) {
            this.needCross = data.needCross;
        }

        if (data.needExtraPadding !== undefined) {
            this.needExtraPadding = data.needExtraPadding;
        }

        if (data.dataQaId !== undefined) {
            this.dataQaId = data.dataQaId;
        }

        if (data.isTertiaryCancel !== undefined) {
            this.isTertiaryCancel = data.isTertiaryCancel;
        }

        if (data.alignButtonWithText !== undefined) {
            this.alignButtonWithText = data.alignButtonWithText;
        }
    }

    close(): void {
        this.onClose.next(true);
        this.dialogRef.close();
    }

    confirm(): void {
        this.onClose.next(false);
    }
}

@Injectable({
    providedIn: 'root'
})
export class ConfirmDialogService {
    constructor(private readonly dialog: MatDialog) {}

    /**
     * @deprecated - Please use the new shared effect
     *
     * @param {string} message
     * @param {string} action
     * @param {string} title
     * @param {boolean} [needCancel]
     * @param {string} [cancelText]
     * @param {SanitizeType} [type]
     * @param {string} [panelClass]
     * @returns {Observable<boolean>}
     * @memberof ConfirmDialogService
     */
    show(
        message: string,
        action: string,
        title: string,
        needCancel?: boolean,
        cancelText?: string,
        type?: SanitizeType,
        panelClass?: string
    ): Observable<boolean> {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '528px',
            minWidth: '400px',
            data: {
                message: message,
                action: action,
                title: title,
                needCancel: needCancel,
                cancelText: cancelText,
                type: type
            },
            panelClass: panelClass ? panelClass : ['nova__dialog', 'nova__dialog--show-above-all']
        });

        return dialogRef.afterClosed();
    }

    showDialog(settings: ConfirmationDialogSettings): Observable<boolean> {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '528px',
            minWidth: '400px',
            disableClose: settings.disableClose ?? false,
            data: {
                message: settings.message,
                action: settings.action,
                title: settings.title,
                needCancel: settings.needCancel,
                cancelText: settings.cancelText,
                type: settings.type,
                addtlData: settings?.addtlData ?? undefined,
                needExtraPadding: settings.needExtraPadding,
                needCross: settings.needCross ?? false,
                dataQaId: settings.dataQaId ?? '',
                isTertiaryCancel: settings.isTertiaryCancel ?? false,
                alignButtonWithText: settings.alignButtonWithText
            },
            panelClass: settings.panelClass ? settings.panelClass : ['nova__dialog', 'nova__dialog--show-above-all'],
            autoFocus: typeof settings.autoFocus === 'boolean' ? settings.autoFocus : true
        });

        return dialogRef.afterClosed();
    }
}
